import React from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import Checkbox from '~/components/shared/CheckBox/CheckBox';
import { Body, Link, notifications } from '@qred/components-library';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { onChangeInputEvent } from '~/types/types';
import { updateForm } from '~/store/slices/loanOffer.slice';
import { logToSentry } from '~/helpers/loggers.helper';
import useTermsAndConditionsModal from '~/hooks/useTermsAndConditionsModal';
import { CountryCode, LoanType } from '~/enums';

const TermsAndConditions = () => {
  const t = useTranslate();
  const dispatch = useAppDispatch();
  const { openTermsAndConditionsModal } = useTermsAndConditionsModal();

  const {
    loanOffer: {
      form,
      overview: { applicationId, granter, loanType },
    },
    intl: { market },
  } = useSelector((state: RootState) => state);

  const onChange = (event: onChangeInputEvent) => {
    const { checked } = event.target;
    dispatch(updateForm({ offerIsAgreed: checked }));
    dispatch(
      pushToGtmLoanAction({
        actionName: 'terms_agreement_change',
      })
    );
  };

  const openModal = () => {
    if (granter) {
      openTermsAndConditionsModal(granter);
    } else {
      notifications.show({
        state: 'critical',
        message: t('SomethingWentWrong'),
        opened: true,
      });
      logToSentry(
        new Error(`Granter is missing for application ${applicationId}`),
        'GranterIsMissingForApplication'
      );
    }
  };

  const isNorwayAnnuityLoan =
    market === CountryCode.NO && loanType === LoanType.ANNUITY_LOAN;

  const openTermsExternal = () => {
    window.open(
      'https://www.qred.no/vilkar-og-betingelser',
      '_blank',
      'noopener noreferrer'
    );
  };

  const openPriceListExternal = () => {
    window.open(
      'https://www.qred.no/bedriftslan-prisliste',
      '_blank',
      'noopener noreferrer'
    );
  };

  const termsAndConditionsLink = isNorwayAnnuityLoan ? (
    <Link size="sm" onClick={openTermsExternal}>
      {t('OnboardingOffer.TermsAndConditionslinkText')}
    </Link>
  ) : (
    <Link size="sm" onClick={openModal}>
      {t('OnboardingOffer.TermsAndConditionslinkText')}
    </Link>
  );

  const priceListingLink = (
    <Link size="sm" onClick={openPriceListExternal}>
      {t('OnboardingOffer.PriceListingLinkText')}
    </Link>
  );

  let consentText: string;
  if (isNorwayAnnuityLoan) {
    consentText = t('OnboardingOffer.TermsAndConditionsConsentWithPriceList', {
      termsAndConditionslinkText: termsAndConditionsLink,
      priceListingLinkText: priceListingLink,
    }) as string;
  } else {
    consentText = t('OnboardingOffer.TermsAndConditionsConsent', {
      termsAndConditionslinkText: termsAndConditionsLink,
    }) as string;
  }

  return (
    <>
      <Checkbox
        checked={form.offerIsAgreed}
        dataCy="terms_and_conditions_checkbox"
        name="offerIsAgreed"
        onChange={onChange}
        validationType="Required"
      >
        <Body size={'sm'}>{consentText}</Body>
      </Checkbox>
    </>
  );
};

export default TermsAndConditions;
